import React, { useState, useEffect } from 'react';
import { Box, Container, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import FeatureModal from './modals/feature/featureModal';
import DeleteFeatureModal from './modals/feature/deleteFeatureModal';
import { toast } from 'react-toastify';
import { sortItems } from '../common/utils/tableUtil';
import PageHeader from '../common/banner/pageHeader';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import SearchBar from '../common/search/SearchBar';

const FeaturesTable = ({ items, sortConfig, onSortChange, setFeatures, fetchFeatures, currentURL }) => {

  const handleSort = (key) => {
    key = key.toLowerCase();
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  }

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  }

  const featureUpdate = (feature) => {
      toast.success(`${feature.name} Updated`);
      fetchFeatures();
  }

  const handleDelete = (feature) =>  {
      setFeatures(items.filter(items => items.id !== feature.id));
      toast.success(`${feature.name} Deleted`, { autoClose: 3000 });
  }

  const formatDate = (date) => {      
      if (date != null) {
        const incDate = new Date(date);
        const year = incDate.getFullYear();
        const month = (1 + incDate.getMonth()).toString().padStart(2, '0');
        const day = incDate.getDate().toString().padStart(2, '0');
        return (month + '/' + day + '/' + year);
      }
      return '';
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['Created', 'Name', 'Description'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}
                {
                    currentURL.includes('/admin') ?
                    (  <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell> ) : ( null )
                }
                
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((feature, index) => (
              <TableRow key={feature.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>{formatDate(feature.created)}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{feature.name}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{feature.description}</TableCell>
                {
                    currentURL.includes('/admin') ?
                    (  
                       <TableCell> 
                          <Stack direction="row" spacing={1}>
                            <FeatureModal feature={feature} onTriggerUpdate={featureUpdate} />
                            <DeleteFeatureModal feature={feature} onTriggerDelete={handleDelete} />                    
                          </Stack>
                        </TableCell>
                    ) : ( null )
                }
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default function Features() {
  const [features, setFeatures] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const title = 'Features';
  const currentURL = window.location.href;

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/features/`);    
      if (!response.ok) {
          toast.error(`HTTP error! status: ${response.status}`);
      } else {
          const json = await response.json();
          setFeatures(json.data);          
      }
    } catch (err) {
      toast.error(`Fetching Features Failed : ${err}`);
    }
  }

  const handleCreate = (feature) =>  {
      toast.success(`${feature.name} Created`, { autoClose: 3000 });
      fetchFeatures();
  }

  const handleSortChange = (newConfig) => {
    setSortConfig(newConfig);
  }

  const filtered = features.filter((item) => {
    const s = searchQuery.toLowerCase();    
    return ['created', 'name', 'description'].some((field) => item[field]?.toLowerCase().includes(s));    
  })

  const sorted = sortItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 5 }}>
        <Box sx={{pt: 2}}>
            <ClassificationBannerHeader></ClassificationBannerHeader>
            <PageHeader title={title}></PageHeader>
            <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>                    
                  <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search Features..."
                      label="Search Features..."
                      width={200}
                  />                
            </Stack>
        
              <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                  <FeaturesTable
                      items={paginated}
                      sortConfig={sortConfig}
                      onSortChange={handleSortChange}
                      setFeatures={setFeatures}
                      fetchFeatures={fetchFeatures}
                      currentURL={currentURL}
                  />
                  <TablePagination
                      component="div"
                      count={sorted.length}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                  />
              </TableContainer>
              <Box>
                  {
                    currentURL.includes('/admin') ?
                    (<FeatureModal feature='' onTriggerCreate={handleCreate} />) : ( null )
                  }
              </Box>
        </Box>                 
    </Container>
  )
}